// ─── What: the probability panel's crosshair, pills and tooltip ───────────────
//
// Binds to the brush's overlay rect — the one element covering the whole plot.
//
// The crosshair MAGNETS: it snaps to the nearest real data point rather than
// following the cursor exactly, so reading a value off it cannot land between
// two candles. The snap searches the RAW series, not the extended one, because
// the extension appends a synthetic point at the live edge that nothing traded
// at.

import * as d3 from 'd3';
import type { OHLCVPoint } from '../../../../api/types';
import type { MarketSeriesData } from '../../../../api/marketPricesClient';
import type { AssetOverlayData } from '../../../../hooks/useAssetPriceOverlay';
import { getAssetColor } from '../../../../hooks/useAssetPriceOverlay';
import { clampToDomain } from '../../chartYDomain';
import { clampLabelPos } from '../../chartAxis';
import { nearestOhlcv, floorOhlcv } from '../../chartData';
import {
    placeTooltip,
    esc,
    buildProbTooltipHtml,
    buildCandleTooltipHtml,
} from '../../chartTooltips';
import { assetPriceScale } from '../panels/drawAssetOverlays';
import { MARGIN } from '../layout';
import type { ChartChrome, ChartGroup, DrawCtx } from '../drawContext';

export interface CrosshairDeps {
    brushGroup: ChartGroup;
    chrome: ChartChrome;
    /** The untrimmed prop series, for snapping. See the note above. */
    rawSeries: MarketSeriesData[];
    loadedAssets: AssetOverlayData[];
    /** Per-asset filtered/extended data, in the same order as loadedAssets. */
    extendedAssetArrays: OHLCVPoint[][];
}

export function attachCrosshair(ctx: DrawCtx, deps: CrosshairDeps): void {
    const {
        layout,
        colors,
        theme,
        xScale,
        yScale,
        xDomain,
        probDomain,
        series,
        selectedIds,
        chartType,
        candleDataFor,
        colorMap,
        questionMap,
        outcomesMap,
        noSideColor,
        showNoLine,
        tooltipRef,
    } = ctx;
    const { brushGroup, chrome, rawSeries, loadedAssets, extendedAssetArrays } =
        deps;
    const { isDark, textColor, tc } = colors;
    const w = layout.w;

    brushGroup
        .select('.overlay')
        .style('cursor', 'crosshair')
        .on('mousemove', (event: MouseEvent) => {
            // Signal pins own their own hover.
            if ((event.target as SVGElement).closest?.('.signal-overlay'))
                return;

            const [mx] = d3.pointer(event);
            const hoverDate = xScale.invert(mx);
            chrome.showCross(mx);

            // ── Snap to the nearest real point across every series ────────
            let snapDate = hoverDate;
            let bestDist = Infinity;
            const hoverMs = hoverDate.getTime();

            for (const s of series) {
                const srcData = rawSeries.find(
                    src => src.conditionId === s.conditionId
                )?.data;
                if (!srcData || srcData.length === 0) continue;
                // A market that had not started trading yet must not pull the snap.
                if (s.firstTradeDate && hoverDate < s.firstTradeDate) continue;
                const candidate = nearestOhlcv(srcData, hoverDate);
                if (!candidate) continue;
                const dist = Math.abs(candidate.t.getTime() - hoverMs);
                if (dist < bestDist) {
                    bestDist = dist;
                    snapDate = candidate.t;
                }
            }

            // Stacked reads its value off the band under the cursor, not off a
            // per-series list, so it collects nothing here.
            const seriesValues = (chartType === 'stacked' ? [] : series)
                .flatMap(s => {
                    if (s.firstTradeDate && hoverDate < s.firstTradeDate)
                        return [];
                    const pt = nearestOhlcv(s.data, hoverDate);
                    if (!pt || pt.t < xDomain[0] || pt.t > xDomain[1])
                        return [];
                    return [
                        { conditionId: s.conditionId, close: pt.close, t: pt.t },
                    ];
                })
                .sort((a, b) => b.close - a.close);

            // ── Magnet the crosshair, then place the axis pills ───────────
            const snapX = xScale(snapDate);
            chrome.crossV.attr('x1', snapX).attr('x2', snapX);

            const primary = seriesValues[0];
            if (primary) {
                const py = clampLabelPos(
                    yScale(clampToDomain(primary.close, probDomain.domain)),
                    MARGIN.top,
                    layout.probHeight,
                    8
                );
                chrome.pricePill.style('display', null);
                const ptxt = chrome.pricePill
                    .select<SVGTextElement>('text')
                    .text(`${(primary.close * 100).toFixed(1)}%`)
                    .attr('x', MARGIN.left + w + 6)
                    .attr('y', py)
                    .attr('text-anchor', 'start');
                const pw = (ptxt.node()?.getComputedTextLength() ?? 30) + 8;
                chrome.pricePill
                    .select('rect')
                    .attr('x', MARGIN.left + w + 3)
                    .attr('y', py - 7.5)
                    .attr('width', pw);
            } else {
                chrome.pricePill.style('display', 'none');
            }

            const tx = clampLabelPos(snapX, MARGIN.left, MARGIN.left + w, 40);
            chrome.timePill.style('display', null);
            const ttxt = chrome.timePill
                .select<SVGTextElement>('text')
                .text(d3.timeFormat('%b %d, %H:%M')(snapDate))
                .attr('x', tx)
                .attr('y', layout.totalHeight - 7)
                .attr('text-anchor', 'middle');
            const tw = (ttxt.node()?.getComputedTextLength() ?? 80) + 8;
            chrome.timePill
                .select('rect')
                .attr('x', tx - tw / 2)
                .attr('y', layout.totalHeight - 14.5)
                .attr('width', tw);

            // ── Asset values ──────────────────────────────────────────────
            const assetValues = loadedAssets.flatMap((asset, ai) => {
                const extData = extendedAssetArrays[ai];
                if (!extData?.length) return [];
                const pt = nearestOhlcv(extData, hoverDate);
                if (!pt || pt.t < xDomain[0] || pt.t > xDomain[1]) return [];
                return [
                    {
                        ticker: asset.ticker,
                        close: pt.close,
                        color: getAssetColor(ai, isDark),
                    },
                ];
            });

            // ── Hover dots ────────────────────────────────────────────────
            chrome.hoverDots.selectAll('*').remove();
            chrome.hoverDots.style('display', null);

            const dotStroke = isDark ? '#000' : '#fff';
            for (const { conditionId, close, t } of seriesValues) {
                chrome.hoverDots
                    .append('circle')
                    .attr('cx', xScale(t))
                    .attr('cy', yScale(clampToDomain(close, probDomain.domain)))
                    .attr('r', 3)
                    .attr('fill', colorMap.get(conditionId) ?? '#999')
                    .attr('stroke', dotStroke)
                    .attr('stroke-width', 1.5);
                if (showNoLine === conditionId) {
                    chrome.hoverDots
                        .append('circle')
                        .attr('cx', xScale(t))
                        .attr(
                            'cy',
                            yScale(clampToDomain(1 - close, probDomain.domain))
                        )
                        .attr('r', 3)
                        .attr('fill', theme.palette.error.main)
                        .attr('stroke', dotStroke)
                        .attr('stroke-width', 1.5)
                        .attr('opacity', 0.7);
                }
            }
            for (const { close, color } of assetValues) {
                // NOTE (carried over unchanged): this re-finds the asset by
                // colour, which picks the wrong index whenever two assets share
                // one. Faithful move — see README.
                const ai = assetValues.findIndex(a => a.color === color);
                const extData = extendedAssetArrays[ai];
                if (extData?.length) {
                    const ps = assetPriceScale(extData, layout.probHeight);
                    chrome.hoverDots
                        .append('circle')
                        .attr('cx', xScale(nearestOhlcv(extData, hoverDate)!.t))
                        .attr('cy', ps(close))
                        .attr('r', 3)
                        .attr('fill', color)
                        .attr('stroke', dotStroke)
                        .attr('stroke-width', 1.5);
                }
            }

            // ── Tooltip, one shape per mode ───────────────────────────────
            if (chartType === 'stacked') {
                // Identify the band under the cursor by walking the cumulative
                // stack, and report that option's share.
                const myPix = d3.pointer(event)[1];
                const pHover = clampToDomain(
                    yScale.invert(myPix),
                    probDomain.domain
                );
                let cum = 0;
                let matched = false;
                let hitLabel = series.length === 1 ? 'No' : 'Other';
                let hitColor = textColor;
                let hitVal = 0;
                for (const s of series) {
                    const pt = floorOhlcv(s.data, hoverDate);
                    const v = pt ? Math.max(0, pt.close) : 0;
                    if (pHover >= cum && pHover < cum + v) {
                        hitLabel = questionMap.get(s.conditionId) ?? '';
                        hitColor = colorMap.get(s.conditionId) ?? '#999';
                        hitVal = v;
                        matched = true;
                        break;
                    }
                    cum += v;
                }
                // Nothing matched: the cursor is in the residual band.
                if (!matched) hitVal = Math.max(0, 1 - cum);
                placeTooltip(
                    tooltipRef.current,
                    event,
                    `<div style="font-family:monospace;font-size:11px;color:${textColor};margin-bottom:4px">` +
                        `${d3.timeFormat('%b %d, %H:%M')(snapDate)}</div>` +
                        `<div style="display:flex;align-items:center;gap:6px;white-space:nowrap">` +
                        `<span style="width:8px;height:8px;border-radius:2px;background:${hitColor};display:inline-block"></span>` +
                        `<span style="flex:1">${esc(hitLabel)}</span>` +
                        `<b style="font-family:monospace;margin-left:8px">${(hitVal * 100).toFixed(1)}%</b>` +
                        `</div>`
                );
            } else if (chartType === 'candles') {
                const candleSeries =
                    series.find(es => selectedIds.includes(es.conditionId)) ??
                    series[0];
                const candle = candleSeries
                    ? nearestOhlcv(candleDataFor(candleSeries), hoverDate)
                    : null;
                if (candle) {
                    placeTooltip(
                        tooltipRef.current,
                        event,
                        buildCandleTooltipHtml(
                            candle,
                            theme.palette.success.main,
                            theme.palette.error.main,
                            tc
                        )
                    );
                }
            } else {
                placeTooltip(
                    tooltipRef.current,
                    event,
                    buildProbTooltipHtml(
                        snapDate,
                        seriesValues,
                        assetValues,
                        colorMap as Map<string, string>,
                        questionMap as Map<string, string>,
                        outcomesMap as Map<string, string[] | null>,
                        tc,
                        showNoLine,
                        noSideColor(
                            showNoLine
                                ? outcomesMap.get(showNoLine) ?? null
                                : null
                        )
                    )
                );
            }
        })
        .on('mouseleave', () => {
            chrome.hideAll();
        });
}
